import { styled, Toolbar, Typography, useTheme, useMediaQuery, Box } from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import { Logo } from 'components/structure/Logo'
import ThemeSwitch from 'components/structure/ThemeSwitch'
import { colors } from 'constants/colors'
import { DARK_THEME, LIGHT_THEME } from 'constants/theme'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { TeamData } from 'types/fpl/teams'
import type { RootState } from 'types/store'

interface AppBarProps extends MuiAppBarProps {
    open?: boolean
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({theme}) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    })
}))

interface mainAppBarProps {
    setTheme: React.Dispatch<any>
}

const MainAppBar: React.FC<mainAppBarProps> = ({setTheme}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const isTablet = useMediaQuery(theme.breakpoints.down('md'))
    const userTeamData: TeamData | null = useSelector((state: RootState) => state.user.teamData)
    const [themeChecked, setThemeChecked] = useState<boolean>(
        window.matchMedia('(prefers-color-scheme: dark)').matches
    )
    const [appBarBGColor, setAppBarBGColor] = useState<string>(colors.RICH_BLACK)

    useEffect(() => {
        if (themeChecked) {
            setTheme(DARK_THEME)
            setAppBarBGColor(colors.ALMOST_BLACK)
        } else {
            setTheme(LIGHT_THEME)
            setAppBarBGColor(colors.RICH_BLACK)
        }
    }, [themeChecked])

    const helloUsername: string = useMemo(() => {
        if (userTeamData?.player_first_name && userTeamData?.player_last_name) {
            return `Hello ${userTeamData.player_first_name} ${userTeamData.player_last_name}!`
        }
        return ''
    }, [userTeamData])


    return (
        <div>
            <AppBar position='static' style={{backgroundColor: appBarBGColor}}>
                <Toolbar 
                    sx={{
                        justifyContent: 'space-between',
                        minHeight: { xs: '56px', sm: '64px' },
                        px: { xs: 1, sm: 2 },
                        flexWrap: { xs: 'nowrap', sm: 'wrap' },
                        gap: { xs: 0.5, sm: 0 }
                    }}
                >
                    <Box 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: { xs: '1 1 auto', sm: '0 1 auto' },
                            minWidth: 0,
                            overflow: 'hidden'
                        }}
                    >
                        <Logo/>
                        <Typography 
                            fontFamily='PremierLeagueBold' 
                            sx={{
                                color: colors.WHITE,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                ml: { xs: 1, sm: 1 }
                            }}
                        >
                            FPL Mini-League Insights
                        </Typography>
                    </Box>
                    <Box 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: { xs: '0 0 auto', sm: '0 1 auto' },
                            gap: { xs: 0.5, sm: 1 },
                            minWidth: 0
                        }}
                    >
                        {!isMobile && helloUsername && (
                            <Typography 
                                fontFamily='PremierLeagueBold' 
                                sx={{
                                    color: colors.WHITE,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: { xs: '120px', sm: '200px', md: 'none' },
                                    display: { xs: 'none', sm: 'block' }
                                }}
                            >
                            {helloUsername}
                        </Typography>
                        )}
                        <ThemeSwitch 
                            sx={{
                                m: { xs: 0.5, sm: 1 },
                                '& .MuiSwitch-root': {
                                    transform: { xs: 'scale(0.8)', sm: 'scale(1)' }
                                }
                            }} 
                            checked={themeChecked}
                            onChange={() => setThemeChecked(!themeChecked)}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
        </div>

    )
}

export default MainAppBar
